//
// Copyright 2026 ArangoDB GmbH, Cologne, Germany
// All rights reserved. See LICENSE.md in the project root for license information.
//

import moment from "moment";
import React, { Component } from "react";
import Downloader from "js-file-downloader";
import { Card, Grid } from "semantic-ui-react";
import {
  EverestInvoice as ApiEverestInvoice,
  EverestInvoiceList as ApiEverestInvoiceList,
  EverestInvoicePaymentState as ApiEverestInvoicePaymentState,
  ListEverestInvoicesRequest as ApiListEverestInvoicesRequest,
  Organization as ApiOrganization,
} from "../../api/lib";
import apiClients from "../../api/apiclients";
import Auth from "../../auth/Auth";
import { ListActionDownload, Section, SectionContent, SectionHead, SectionHeader, UnderlinedH3 } from "../../ui/lib";
import { reportError } from "../../errors/reporting";

// The API returns enum values as strings in JSON (e.g. "EVEREST_INVOICE_PAYMENT_STATE_UNPAID"),
// while the generated TS enum is numeric — so payment_state must be normalized before comparing,
// otherwise every case misses and the status renders blank.
const normalizePaymentState = (state?: ApiEverestInvoicePaymentState | string | null): ApiEverestInvoicePaymentState | undefined => {
  if (state == null || state === "") {
    return ApiEverestInvoicePaymentState.EVEREST_INVOICE_PAYMENT_STATE_UNSPECIFIED;
  }
  if (typeof state === "number") {
    return state;
  }
  const stateFromEnumName = ApiEverestInvoicePaymentState[state as keyof typeof ApiEverestInvoicePaymentState];
  if (typeof stateFromEnumName === "number") {
    return stateFromEnumName;
  }
  const numericState = Number(state);
  if (Number.isFinite(numericState)) {
    return numericState;
  }
  return undefined;
};

const stateText = (inv: ApiEverestInvoice) => {
  if (inv.is_overdue) {
    return "Overdue";
  }
  switch (normalizePaymentState(inv.payment_state)) {
    case ApiEverestInvoicePaymentState.EVEREST_INVOICE_PAYMENT_STATE_UNPAID:
      return "Unpaid";
    case ApiEverestInvoicePaymentState.EVEREST_INVOICE_PAYMENT_STATE_PARTIALLY_PAID:
      return "Partially paid";
    case ApiEverestInvoicePaymentState.EVEREST_INVOICE_PAYMENT_STATE_PAID_IN_FULL:
      return "Paid";
    case ApiEverestInvoicePaymentState.EVEREST_INVOICE_PAYMENT_STATE_CREDITED_IN_FULL:
      return "Credited";
    default:
      return "";
  }
};

const fmtAmount = (a?: { amount?: string; currency?: string }) => (a && a.amount ? `${a.amount} ${a.currency || ""}` : "-");

interface IEverestInvoiceViewArgs {
  invoice: ApiEverestInvoice;
  onDownload: () => void;
}

const EverestInvoiceView = ({ ...args }: IEverestInvoiceViewArgs) => (
  <Card>
    <Card.Content>
      <Grid width="16">
        <Grid.Column width="3">
          <UnderlinedH3>Number</UnderlinedH3>
          <div>{args.invoice.invoice_number}</div>
        </Grid.Column>
        <Grid.Column width="3">
          <UnderlinedH3>Date</UnderlinedH3>
          <div>{args.invoice.invoice_date ? moment(args.invoice.invoice_date).format("YYYY-MM-DD") : "-"}</div>
        </Grid.Column>
        <Grid.Column width="3">
          <UnderlinedH3>Due date</UnderlinedH3>
          <div>{args.invoice.due_date ? moment(args.invoice.due_date).format("YYYY-MM-DD") : "-"}</div>
        </Grid.Column>
        <Grid.Column width="2">
          <UnderlinedH3>Amount</UnderlinedH3>
          <div>{fmtAmount(args.invoice.total_amount)}</div>
        </Grid.Column>
        <Grid.Column width="2">
          <UnderlinedH3>Amount due</UnderlinedH3>
          <div>{fmtAmount(args.invoice.total_amount_due)}</div>
        </Grid.Column>
        <Grid.Column width="2">
          <UnderlinedH3>Status</UnderlinedH3>
          <div style={args.invoice.is_overdue ? { color: "red", fontWeight: "bold" } : undefined}>{stateText(args.invoice)}</div>
        </Grid.Column>
        <Grid.Column textAlign="right">
          <ListActionDownload onClick={args.onDownload} />
        </Grid.Column>
      </Grid>
    </Card.Content>
  </Card>
);

interface IEverestInvoicesProps {
  auth: Auth;
  organization: ApiOrganization;
  refreshNow: ((callback: () => Promise<void>) => Promise<void>) | undefined;
  // Reports whether the org has any new-billing (Everest) invoices, so the parent
  // can rename the V1 section to "Invoices (legacy billing)" when it does.
  onHasInvoicesChange?: (has: boolean) => void;
}

interface IEverestInvoicesState {
  list?: ApiEverestInvoiceList;
  loading: boolean;
  errorMessage?: unknown;
  refreshNeeded: boolean;
  last_organization_id?: string;
}

// EverestInvoices lists the organization's invoices from the new billing
// system. Renders nothing while the organization has none (V1-only orgs).
class EverestInvoices extends Component<IEverestInvoicesProps, IEverestInvoicesState> {
  state: IEverestInvoicesState = { loading: false, refreshNeeded: false };

  static getDerivedStateFromProps(props: IEverestInvoicesProps, state: IEverestInvoicesState) {
    if (props.organization.id != state.last_organization_id) {
      return {
        list: undefined,
        refreshNeeded: true,
        loading: false,
        last_organization_id: props.organization.id,
      };
    }
    // No state update necessary
    return null;
  }

  componentDidUpdate() {
    if (this.state.refreshNeeded) {
      this.setState({ refreshNeeded: false }, this.refreshInvoices);
    }
  }

  refreshInvoices = () => {
    this.props.refreshNow && this.props.refreshNow(this.reloadInvoices);
  };

  reloadInvoices = async () => {
    const req = { organization_id: this.props.organization.id } as ApiListEverestInvoicesRequest;
    const list = await apiClients.billingClient.ListEverestInvoices(req);
    this.setState({ list });
    this.props.onHasInvoicesChange && this.props.onHasInvoicesChange(((list || {}).items || []).length > 0);
  };

  componentDidMount() {
    this.refreshInvoices();
  }

  onDownloadInvoice = async (inv: ApiEverestInvoice) => {
    try {
      this.setState({ loading: true });
      const fileURL = `/downloads/everest-invoices/${inv.invoice_uuid || ""}.pdf`;
      // Save the file as "<invoice date>_<invoice number>.pdf" rather than the raw UUID.
      const datePart = inv.invoice_date ? moment(inv.invoice_date).format("YYYY.MM.DD") : "";
      const numberPart = (inv.invoice_number || inv.invoice_uuid || "invoice").replace(/[^\w.-]+/g, "-");
      const filename = `${[datePart, numberPart].filter(Boolean).join("_")}.pdf`;
      const downloader = new Downloader({
        url: fileURL,
        filename,
        headers: [{ name: "Authorization", value: `bearer ${this.props.auth.getAccessToken()}` }],
      });
      await downloader;
    } catch (e: unknown) {
      this.setState({ errorMessage: e });
      reportError(e);
    } finally {
      this.setState({ loading: false });
    }
  };

  render() {
    const items = (this.state.list || {}).items || [];
    if (items.length == 0) {
      // No new-billing invoices (yet): keep the section invisible.
      return null;
    }
    return (
      <Section>
        <SectionHead>
          <SectionHeader title="Invoices" />
        </SectionHead>
        <SectionContent>
          <Card.Group itemsPerRow="1">
            {items.map((x) => (
              <EverestInvoiceView key={x.invoice_uuid} invoice={x} onDownload={() => this.onDownloadInvoice(x)} />
            ))}
          </Card.Group>
        </SectionContent>
      </Section>
    );
  }
}

export default EverestInvoices;
